import { useEffect, useState, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Inbox } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { apiFetch } from '../lib/api';
import { track } from '../lib/analytics';
import { FeedCard, type FeedWithCount } from '../components/FeedCard';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { SignalLogo } from '../components/SignalLogo';
import { cn } from '../lib/utils';

export function DashboardPage() {
  const { t } = useTranslation('dashboard');
  const { t: tc } = useTranslation('common');
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [feeds, setFeeds] = useState<FeedWithCount[]>([]);
  const [loading, setLoading] = useState(true);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement | null>(null);

  const loadFeeds = useCallback(async () => {
    const res = await apiFetch('/feeds');
    if (res.ok) {
      const data = (await res.json()) as FeedWithCount[];
      setFeeds(data);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    track('page_viewed', { page: 'dashboard' });
    loadFeeds();
  }, [loadFeeds]);

  useEffect(() => {
    if (!userMenuOpen) return;
    const handlePointerDown = (event: MouseEvent) => {
      if (!userMenuRef.current?.contains(event.target as Node)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [userMenuOpen]);

  const handleFeedUpdated = (updated: FeedWithCount) => {
    setFeeds((prev) => prev.map((f) => (f.id === updated.id ? updated : f)));
  };

  const handleFeedDeleted = (feedId: string) => {
    setFeeds((prev) => prev.filter((f) => f.id !== feedId));
  };

  return (
    <div className="min-h-screen bg-brand-canvas">
      {/* Sticky header bar */}
      <header className="sticky top-0 z-50 border-b border-brand-border bg-white/80 backdrop-blur-[20px]">
        <div className="mx-auto flex h-14 max-w-[880px] items-center justify-between px-4 sm:px-6">
          <Link to="/dashboard" className="flex items-center gap-2 no-underline">
            <SignalLogo size={26} />
            <span className="text-[19px] font-semibold leading-7 tracking-[-0.02em] text-brand-text">
              {tc('signal')}
            </span>
          </Link>
          <div className="relative" ref={userMenuRef}>
            <button
              onClick={() => setUserMenuOpen((open) => !open)}
              aria-expanded={userMenuOpen}
              className="flex cursor-pointer items-center gap-1.5 rounded-lg border-0 bg-transparent px-1 py-1.5 hover:bg-black/[0.04]"
            >
              <span className="text-sm font-medium leading-5 text-brand-text">
                {user?.email?.split('@')[0]}
              </span>
              <ChevronDown
                size={12}
                strokeWidth={2}
                className={cn(
                  'text-brand-muted transition-transform duration-150',
                  userMenuOpen && 'rotate-180',
                )}
              />
            </button>
            {userMenuOpen && (
              <div className="absolute right-0 top-[calc(100%+8px)] z-20 min-w-[220px] rounded-xl border border-brand-border bg-white p-2 shadow-[0_8px_24px_rgba(0,0,0,0.1)]">
                <div className="mb-1.5 border-b border-brand-border px-2.5 py-2 text-[13px] text-brand-muted">
                  {user?.email}
                </div>
                <div className="flex items-center justify-between px-2.5 py-1.5">
                  <span className="text-[13.5px] text-brand-text">{t('menu.language')}</span>
                  <LanguageSwitcher />
                </div>
                <button
                  onClick={signOut}
                  className="w-full cursor-pointer rounded-[7px] border-0 bg-transparent px-2.5 py-2 text-left text-[13.5px] font-medium text-brand-error hover:bg-brand-error/[0.08]"
                >
                  {t('logout')}
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[880px] px-4 py-10 pb-20 sm:px-6">
        {/* Page title row */}
        <div className="mb-7 flex flex-wrap items-center justify-between gap-3">
          <h1 className="m-0 whitespace-nowrap text-[28px] font-semibold leading-9 tracking-[-0.015em] text-brand-text sm:text-[32px]">
            {t('title')}
          </h1>
          {(loading || feeds.length > 0) && (
            <button
              onClick={() => navigate('/feeds/new')}
              className="inline-flex h-10 cursor-pointer items-center rounded-full border-0 bg-accent-blue px-5 text-sm font-medium leading-5 text-white hover:bg-accent-blue-hover"
            >
              {t('createFeed')}
            </button>
          )}
        </div>

        {/* Loading skeleton */}
        {loading && (
          <div className="flex flex-col gap-4">
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className="rounded-2xl border border-brand-border bg-white p-6 shadow-[0_1px_3px_rgba(0,0,0,0.04)]"
              >
                <div className="mb-4 flex items-center justify-between">
                  <div className="h-5 w-40 animate-pulse rounded bg-brand-surface" />
                  <div className="h-5 w-16 animate-pulse rounded bg-brand-surface" />
                </div>
                <div className="mb-3 h-4 w-32 animate-pulse rounded bg-brand-surface" />
                <div className="mb-4 h-4 w-48 animate-pulse rounded bg-brand-surface" />
                <div className="h-9 w-full animate-pulse rounded-lg bg-brand-surface" />
              </div>
            ))}
          </div>
        )}

        {/* Empty state */}
        {!loading && feeds.length === 0 && (
          <div className="rounded-2xl border border-brand-border bg-white px-6 py-16 text-center shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-brand-surface text-brand-muted">
              <Inbox size={28} strokeWidth={1.5} />
            </div>
            <h2 className="mb-2 text-xl font-semibold leading-7 tracking-[-0.01em] text-brand-text">
              {t('empty.title')}
            </h2>
            <p className="mx-auto mb-6 max-w-md text-base leading-relaxed text-brand-subtle">
              {t('empty.message')}
            </p>
            <button
              onClick={() => navigate('/feeds/new')}
              className="inline-flex h-11 cursor-pointer items-center rounded-full border-0 bg-accent-blue px-6 text-sm font-medium leading-5 text-white hover:bg-accent-blue-hover"
            >
              {t('empty.createFirst')}
            </button>
          </div>
        )}

        {/* Feed list */}
        {!loading && feeds.length > 0 && (
          <div className="flex flex-col gap-4">
            {feeds.map((feed) => (
              <FeedCard
                key={feed.id}
                feed={feed}
                onUpdate={handleFeedUpdated}
                onDelete={handleFeedDeleted}
              />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
